import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ShowHideColumnsMenuItems } from '@dealstack/frontend/shared/components/Table/MantineTable/ShowHideColumnsButton/ShowHideColumnsMenuItems';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Flex, Menu, Text, rem } from '@mantine/core';
import { Table } from '@tanstack/react-table';

interface Props<TData extends RecordAny> {
	table: Table<TData>;
	closeMenu?: () => void;
	customRenderer?: (table: Table<TData>) => JSX.Element;
}

export const ShowHideColumnsMenu = <TData extends RecordAny>({
	table,
	closeMenu,
	customRenderer,
}: Props<TData>) => {
	const tCommonUi = useTranslationCommonUi();
	// Computed every render (not memoized on `table`, which is a stable ref): dynamic columns added
	// after the table mounts must still surface here once they exist.
	const raw =
		typeof table.getAllLeafColumns === 'function'
			? table.getAllLeafColumns()
			: table.getAllColumns?.() ?? [];
	const allColumns = raw.filter(
		(column) => column.id !== 'actions' && column.id !== 'transactionError'
	);

	return (
		<Menu.Dropdown
			sx={{
				padding: 0,
			}}
		>
			<Menu.Item onClick={() => closeMenu && closeMenu()}>
				<Flex
					align="center"
					justify="space-between"
					gap="xs"
					sx={(theme) => ({
						padding: theme.spacing.sm,
					})}
				>
					<Flex align="center" gap="xs">
						<Icon
							variant="gear"
							fillStroke
							size="xl"
							color={defaultTheme.v3.colors.blue[6]}
						/>
						<Text
							size="B6Bold"
							sx={(theme) => ({
								color: theme.colors.blue[6],
							})}
						>
							{tCommonUi('mantine_table.top_toolbar.settings')}
						</Text>
					</Flex>
					<Icon variant="caret-up" size="lg" color={defaultTheme.v3.colors.blue[6]} />
				</Flex>
			</Menu.Item>

			<Menu.Divider
				sx={{
					margin: 0,
				}}
			/>
			<div
				style={{
					padding: `${rem('8px')} 0`,
					minWidth: rem('200px'),
					maxHeight: rem('300px'),
					overflowY: 'scroll',
				}}
			>
				<ShowHideColumnsMenuItems
					allColumns={allColumns}
					customRenderer={customRenderer}
					table={table}
				/>
			</div>
		</Menu.Dropdown>
	);
};
